import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import Avatar from "@/components/Avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, Plus } from "lucide-react";

export default function QuickAssign({ task, members, memberMap, onSaved, open: openProp, onOpenChange }) {
  const [openInternal, setOpenInternal] = useState(false);
  const open = openProp !== undefined ? openProp : openInternal;
  const setOpen = onOpenChange || setOpenInternal;
  const [busy, setBusy] = useState(false);
  const [assigned, setAssigned] = useState(task.assigned_to || []);

  // Re-sync when the parent reloads the task (e.g. after a save elsewhere).
  useEffect(() => { setAssigned(task.assigned_to || []); }, [task.assigned_to]);

  const toggle = async (uid) => {
    // Compute from the latest local state so quickly picking several people
    // in a row doesn't overwrite each other with a stale prop value.
    const next = assigned.includes(uid) ? assigned.filter((x) => x !== uid) : [...assigned, uid];
    setAssigned(next); // optimistic
    setBusy(true);
    try {
      await api.patch(`/tasks/${task.task_id}`, { assigned_to: next });
      onSaved && onSaved();
    } catch (e) {
      setAssigned(task.assigned_to || []); // revert on failure
      toast.error(apiErr(e, "Could not update assignees"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className="flex -space-x-1.5 items-center hover:opacity-80 transition-opacity"
          data-testid={`quick-assign-${task.task_id}`}
        >
          {assigned.slice(0, 3).map((uid) => (
            <Avatar key={uid} id={uid} name={memberMap[uid]?.name || "?"} size={24} className="ring-2 ring-white" />
          ))}
          {assigned.length === 0 && (
            <span className="inline-flex items-center gap-0.5 text-[11px] text-zinc-400 border border-dashed border-zinc-300 rounded-full px-2 py-0.5 hover:border-zinc-500 hover:text-zinc-600 transition-colors">
              <Plus className="h-3 w-3" />Assign
            </span>
          )}
          {assigned.length > 3 && (
            <span className="h-6 w-6 rounded-full bg-zinc-200 text-[10px] flex items-center justify-center ring-2 ring-white">+{assigned.length - 3}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0" onClick={(e) => e.stopPropagation()} data-testid="quick-assign-popover">
        <div className="px-3 py-2 border-b border-zinc-200 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
          Assign to
        </div>
        <div className="max-h-56 overflow-y-auto">
          {members.length === 0 && <p className="p-3 text-sm text-zinc-400">No members</p>}
          {members.map((m) => {
            const on = assigned.includes(m.user_id);
            return (
              <button
                key={m.user_id}
                type="button"
                disabled={busy}
                onClick={() => toggle(m.user_id)}
                className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-zinc-50 disabled:opacity-50 transition-colors"
                data-testid={`quick-assign-member-${m.user_id}`}
              >
                <span className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-zinc-900 border-zinc-900" : "border-zinc-300"}`}>
                  {on && <Check className="h-3 w-3 text-white" />}
                </span>
                <Avatar id={m.user_id} name={m.name} size={24} />
                <span className="text-sm truncate flex-1 text-left">{m.name}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
